import {ChipsOption} from 'platform/components';
import {match} from 'ts-pattern';

import {defaultTo} from 'ramda';

import {useGetAuditDataQuery} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceCheckInQuery,
  useGetServiceHandOverQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';

export const useGetMessageTypeOptions = (serviceCaseId: string) => {
  const serviceCaseQuery = useGetMetadaServiceCaseQuery({
    serviceCaseId,
  });

  const serviceCheckinQuery = useGetServiceCheckInQuery({serviceCaseId});
  const serviceHandoverQuery = useGetServiceHandOverQuery({serviceCaseId});

  const checkingInspectionId = defaultTo('', serviceCheckinQuery.data?.inspectionId?.[0]);
  const handoverInspectionId = defaultTo('', serviceHandoverQuery.data?.inspectionId?.[0]);

  const checkinAuditDataQuery = useGetAuditDataQuery(
    {auditId: checkingInspectionId},
    {skip: !checkingInspectionId, refetchOnMountOrArgChange: true}
  );
  const handoverAuditDataQuery = useGetAuditDataQuery(
    {auditId: handoverInspectionId},
    {skip: !handoverInspectionId, refetchOnMountOrArgChange: true}
  );

  const currentUserQuery = useGetCurrentUserInfoQuery();

  const isLoading =
    serviceCaseQuery.isLoading ||
    serviceCheckinQuery.isLoading ||
    serviceHandoverQuery.isLoading ||
    checkinAuditDataQuery.isLoading ||
    handoverAuditDataQuery.isLoading ||
    currentUserQuery.isLoading;

  const isError =
    serviceCaseQuery.isError ||
    serviceCheckinQuery.isError ||
    serviceHandoverQuery.isError ||
    checkinAuditDataQuery.isError ||
    handoverAuditDataQuery.isError ||
    currentUserQuery.isError;

  const checkinAudit = checkinAuditDataQuery?.data?.find(
    (item) => item.id === checkingInspectionId
  );
  const isCheckinFinished = checkinAudit?.state === 'finished_audit';

  const handoverAudit = handoverAuditDataQuery?.data?.find(
    (item) => item.id === handoverInspectionId
  );
  const isHandoverFinished = handoverAudit?.state === 'finished_audit';

  const [isCustomDisabled, isCheckinDisabled, isHandoverDisabled] = match(
    serviceCaseQuery.data?.state
  )
    .with('CREATED', () => [false, true, true])
    .with('DRAFT', () => [false, true, true])
    .with('DEMAND', () => [false, true, true])
    .with('PLANNED', () => [false, true, true])
    .with('CHECKED_IN', () => [false, false, true])
    .with('HANDED_OVER', () => [false, false, false])
    .otherwise(() => [true, true, true]);

  const messageTypeOptions: ChipsOption[] = [
    {
      label: i18n.t('general.labels.custom'),
      value: 'customer-message',
      isDisabled: isCustomDisabled,
    },
    {
      label: i18n.t('entity.workshop.labels.checkIn'),
      value: 'service-customer_check_in',
      isDisabled: !isCheckinFinished || isCheckinDisabled,
    },
    {
      label: i18n.t('entity.workshop.labels.handover'),
      value: 'service-customer_handover',
      isDisabled: !isHandoverFinished || isHandoverDisabled,
    },
  ];

  return {
    messageTypeOptions,
    isLoading,
    isError,
  };
};
